// The level schema (docs/02-Game-Design/Level Data Format.md, Level Data
// Examples.md). One Zod definition per artifact; the TypeScript types are
// inferred from it. The play artifact is what the game downloads; the
// authoring record is the private designer file with the puzzle solutions.
import { z } from 'zod';
import { isSingleLetter } from './letters';
import { dist2 } from '../geometry/segments';
import { PLANET_KINDS, PLANET_MARGIN, PLANET_SPACING, SKY_ARTS, SKY_HEIGHT, SKY_PRESETS, skyWidth, STAR_EDGE } from './presets';

export const LANGS = ['en', 'de'] as const;
export type Lang = (typeof LANGS)[number];

export const MODES = ['puzzle', 'apogee', 'assemblage'] as const;
export type Mode = (typeof MODES)[number];

/**
 * A puzzle level is keyed by its puzzle type (docs/01-Product/Daily
 * Structure.md); Apogee and Assemblage each have a single variant of their
 * own name (docs/02-Game-Design/Apogee Mode.md, Assemblage Mode.md). The
 * time-of-night slots (twilight, moonrise, deep night) were retired on
 * 2026-10-19; Apogee was High Score until 2026-10-29, and had a Timed variant
 * beside the untimed one until 2026-11-03.
 */
export const PUZZLE_VARIANTS = ['phrase', 'quotation', 'category'] as const;
export const APOGEE_VARIANTS = ['apogee'] as const;
export const ASSEMBLAGE_VARIANTS = ['assemblage'] as const;
export const VARIANTS = [...PUZZLE_VARIANTS, ...APOGEE_VARIANTS, ...ASSEMBLAGE_VARIANTS] as const;
export type PuzzleVariant = (typeof PUZZLE_VARIANTS)[number];
export type ApogeeVariant = (typeof APOGEE_VARIANTS)[number];
export type AssemblageVariant = (typeof ASSEMBLAGE_VARIANTS)[number];
export type Variant = (typeof VARIANTS)[number];

export function modeOf(variant: Variant): Mode {
  if ((PUZZLE_VARIANTS as readonly string[]).includes(variant)) return 'puzzle';
  if ((ASSEMBLAGE_VARIANTS as readonly string[]).includes(variant)) return 'assemblage';
  return 'apogee';
}

const NodeId = z.number().int().positive();
const Coord = z.number().int();

const Letter = z.string().refine(isSingleLetter, 'one uppercase NFC code point');

export const StarNodeSchema = z.object({
  id: NodeId,
  type: z.literal('star'),
  letter: Letter,
  x: Coord,
  y: Coord,
});

/**
 * Every node is a lettered star. The letter and word multiplier objects
 * (2026-10-15 to 2026-10-29) are gone; Apogee's planet took their place and
 * is not a node (docs/02-Game-Design/Core Mechanics.md, Planets).
 */
export const NodeSchema = StarNodeSchema;

export type StarNode = z.infer<typeof StarNodeSchema>;
export type LevelNode = z.infer<typeof NodeSchema>;

export function isStar(node: LevelNode): node is StarNode {
  return node.type === 'star';
}

/**
 * A planet (Apogee only): not a node, never tapped. A constellation whose
 * lines, with one closing line, ring it and nothing else activates it, and
 * the stars on that loop score double (docs/02-Game-Design/Scoring.md).
 * Its kind is which planet it shows (all alike in play for now, Aaron
 * 2026-11-03); a planet saved before kinds has none and shows as Saturn.
 */
export const PlanetSchema = z.object({
  id: NodeId,
  x: Coord,
  y: Coord,
  kind: z.enum(PLANET_KINDS).optional(),
});
export type Planet = z.infer<typeof PlanetSchema>;

export const SkySchema = z.object({
  preset: z.enum(SKY_PRESETS),
  art: z.enum(SKY_ARTS),
});

export const RulesSchema = z.object({
  minWordLength: z.number().int().min(1),
  /** Per-level override of R_block; prefer the global constant. */
  blockRadius: z.number().int().positive().optional(),
});

const Blanks = z.array(z.number().int().positive()).min(1);

// The three puzzle types (docs/02-Game-Design/Puzzle Mode.md): a titled
// phrase, a quotation with its attribution, or a category; the texts carry
// {n} placeholders for the solution words.
// A phrase's `display` is the sentence as written, with the words in their
// natural case ("Many hands make light work."), for titles and for showing
// the phrase once solved; the `phrase` holds the {n} placeholders (2026-10-26).
export const PuzzlePlaySchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('category'), category: z.string().min(1), blanks: Blanks }),
  z.object({ type: z.literal('phrase'), title: z.string().min(1), phrase: z.string().min(1), display: z.string().optional(), blanks: Blanks }),
  z.object({ type: z.literal('quotation'), quotation: z.string().min(1), attribution: z.string().min(1), blanks: Blanks }),
]);

export const SolutionSchema = z.object({
  word: z.string().min(1),
  nodes: z.array(NodeId).min(1),
});

export const PuzzleAuthoringSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('category'), category: z.string().min(1), solutions: z.array(SolutionSchema).min(1) }),
  z.object({
    type: z.literal('phrase'),
    title: z.string().min(1),
    phrase: z.string().min(1),
    display: z.string().optional(),
    solutions: z.array(SolutionSchema).min(1),
  }),
  z.object({
    type: z.literal('quotation'),
    quotation: z.string().min(1),
    attribution: z.string().min(1),
    solutions: z.array(SolutionSchema).min(1),
  }),
]);

const base = {
  schema: z.literal(1),
  id: z.uuid(),
  lang: z.enum(LANGS),
  // Artifacts published between 2026-10-19 and 2026-10-26 carry `access`;
  // free and premium went on 2026-11-01, and unknown keys are dropped on
  // parse, so those immutable files still read.
  sky: SkySchema,
  rules: RulesSchema,
  nodes: z.array(NodeSchema).min(1),
};

type Issue = { path: (string | number)[]; message: string };

function checkLevelShape(level: {
  sky: { preset: (typeof SKY_PRESETS)[number] };
  nodes: LevelNode[];
  planets?: Planet[];
  mode: Mode;
  variant: string;
  puzzle?: { type: string };
}): Issue[] {
  const issues: Issue[] = [];
  const width = skyWidth(level.sky.preset);
  const seen = new Set<number>();
  level.nodes.forEach((node, i) => {
    if (seen.has(node.id)) issues.push({ path: ['nodes', i, 'id'], message: `duplicate node id ${node.id}` });
    seen.add(node.id);
    if (node.x < STAR_EDGE || node.x > width - STAR_EDGE || node.y < STAR_EDGE || node.y > SKY_HEIGHT - STAR_EDGE) {
      issues.push({ path: ['nodes', i], message: `node ${node.id} lies within ${STAR_EDGE} of the ${level.sky.preset} sky's edge` });
    }
  });
  // A planet must be ringable: well inside the sky, and clear of every star
  // and other planet by the node spacing.
  const planets = level.planets ?? [];
  const seenPlanets = new Set<number>();
  const seenKinds = new Set<string>();
  const s2 = PLANET_SPACING * PLANET_SPACING;
  planets.forEach((planet, i) => {
    if (seenPlanets.has(planet.id)) issues.push({ path: ['planets', i, 'id'], message: `duplicate planet id ${planet.id}` });
    seenPlanets.add(planet.id);
    if (planet.kind !== undefined) {
      if (seenKinds.has(planet.kind)) issues.push({ path: ['planets', i, 'kind'], message: `${planet.kind} appears twice on the sky` });
      seenKinds.add(planet.kind);
    }
    if (planet.x < PLANET_MARGIN || planet.x > width - PLANET_MARGIN || planet.y < PLANET_MARGIN || planet.y > SKY_HEIGHT - PLANET_MARGIN) {
      issues.push({ path: ['planets', i], message: `planet ${planet.id} lies within ${PLANET_MARGIN} of the ${level.sky.preset} sky's edge` });
    }
    for (const node of level.nodes) {
      if (dist2(planet, node) < s2) {
        issues.push({ path: ['planets', i], message: `planet ${planet.id} sits within ${PLANET_SPACING} of star ${node.id}` });
        break;
      }
    }
    for (const other of planets.slice(0, i)) {
      if (dist2(planet, other) < s2) {
        issues.push({ path: ['planets', i], message: `planet ${planet.id} sits within ${PLANET_SPACING} of planet ${other.id}` });
        break;
      }
    }
  });
  if (level.mode === 'puzzle' && level.puzzle && level.puzzle.type !== level.variant) {
    issues.push({ path: ['variant'], message: `a ${level.puzzle.type} puzzle is the ${level.puzzle.type} variant` });
  }
  return issues;
}

/**
 * The time-of-night variants retired on 2026-10-19. Artifacts published under
 * them are immutable and plays still point at them, so a puzzle level that
 * names one reads as its own puzzle type.
 */
export const LEGACY_PUZZLE_VARIANTS = ['twilight', 'moonrise', 'deep-night'] as const;

/**
 * Apogee's untimed variant was `untimed` until Timed was removed on
 * 2026-11-03 and the one variant left took the mode's name; an Apogee
 * artifact published as `untimed` reads as `apogee`. A `timed` one is refused.
 */
export const LEGACY_APOGEE_VARIANT = 'untimed';

function withLegacyVariant(data: unknown): unknown {
  if (typeof data !== 'object' || data === null) return data;
  const level = data as { mode?: unknown; variant?: unknown; puzzle?: { type?: unknown } };
  if (level.mode === 'apogee' && level.variant === LEGACY_APOGEE_VARIANT) return { ...level, variant: 'apogee' };
  if (level.mode !== 'puzzle' || !(LEGACY_PUZZLE_VARIANTS as readonly unknown[]).includes(level.variant)) return data;
  return typeof level.puzzle?.type === 'string' ? { ...level, variant: level.puzzle.type } : data;
}

function withShapeChecks<T extends z.ZodTypeAny>(schema: T) {
  return z.preprocess(withLegacyVariant, schema).superRefine((level, ctx) => {
    for (const issue of checkLevelShape(level as Parameters<typeof checkLevelShape>[0])) {
      ctx.addIssue({ code: 'custom', path: issue.path, message: issue.message });
    }
  });
}

export const PuzzlePlayLevelSchema = z.object({
  ...base,
  version: z.number().int().positive(),
  mode: z.literal('puzzle'),
  variant: z.enum(PUZZLE_VARIANTS),
  puzzle: PuzzlePlaySchema,
});

export const ApogeePlayLevelSchema = z.object({
  ...base,
  version: z.number().int().positive(),
  mode: z.literal('apogee'),
  variant: z.enum(APOGEE_VARIANTS),
  planets: z.array(PlanetSchema),
});

export const AssemblagePlayLevelSchema = z.object({
  ...base,
  version: z.number().int().positive(),
  mode: z.literal('assemblage'),
  variant: z.enum(ASSEMBLAGE_VARIANTS),
});

/** The play artifact: what the game downloads and the Worker replays against. */
export const PlayLevelSchema = withShapeChecks(
  z.discriminatedUnion('mode', [PuzzlePlayLevelSchema, ApogeePlayLevelSchema, AssemblagePlayLevelSchema]),
);

export const PublishedSchema = z
  .object({ version: z.number().int().positive(), contentHash: z.string(), publishedAt: z.string() })
  .nullable();

const authoringMeta = z.looseObject({
  author: z.string().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  notes: z.string().optional(),
});

export const PuzzleAuthoringLevelSchema = z.object({
  ...base,
  mode: z.literal('puzzle'),
  variant: z.enum(PUZZLE_VARIANTS),
  puzzle: PuzzleAuthoringSchema,
  published: PublishedSchema,
  meta: authoringMeta,
});

export const ApogeeAuthoringLevelSchema = z.object({
  ...base,
  mode: z.literal('apogee'),
  variant: z.enum(APOGEE_VARIANTS),
  planets: z.array(PlanetSchema),
  published: PublishedSchema,
  meta: authoringMeta,
});

export const AssemblageAuthoringLevelSchema = z.object({
  ...base,
  mode: z.literal('assemblage'),
  variant: z.enum(ASSEMBLAGE_VARIANTS),
  published: PublishedSchema,
  meta: authoringMeta,
});

/** The authoring record: the designer's file, with puzzle solutions. Never shipped. */
export const AuthoringLevelSchema = withShapeChecks(
  z.discriminatedUnion('mode', [PuzzleAuthoringLevelSchema, ApogeeAuthoringLevelSchema, AssemblageAuthoringLevelSchema]),
);

export type PuzzlePlayLevel = z.infer<typeof PuzzlePlayLevelSchema>;
export type ApogeePlayLevel = z.infer<typeof ApogeePlayLevelSchema>;
export type AssemblagePlayLevel = z.infer<typeof AssemblagePlayLevelSchema>;
export type PlayLevel = PuzzlePlayLevel | ApogeePlayLevel | AssemblagePlayLevel;
export type PuzzleAuthoringLevel = z.infer<typeof PuzzleAuthoringLevelSchema>;
export type ApogeeAuthoringLevel = z.infer<typeof ApogeeAuthoringLevelSchema>;
export type AssemblageAuthoringLevel = z.infer<typeof AssemblageAuthoringLevelSchema>;
export type AuthoringLevel = PuzzleAuthoringLevel | ApogeeAuthoringLevel | AssemblageAuthoringLevel;
export type Solution = z.infer<typeof SolutionSchema>;

export function parsePlayLevel(data: unknown): PlayLevel {
  return PlayLevelSchema.parse(data) as PlayLevel;
}

export function parseAuthoringLevel(data: unknown): AuthoringLevel {
  return AuthoringLevelSchema.parse(data) as AuthoringLevel;
}

/**
 * Derive the play artifact from an authoring record: drop meta, published and
 * the solutions; add the version and the blanks (word lengths in order).
 */
export function toPlayArtifact(authoring: AuthoringLevel, version: number): PlayLevel {
  const { meta: _meta, published: _published, ...rest } = authoring;
  if (rest.mode === 'puzzle') {
    const { solutions, ...puzzleRest } = rest.puzzle;
    const puzzle = { ...puzzleRest, blanks: solutions.map((s) => Array.from(s.word).length) };
    return { ...rest, version, puzzle } as PuzzlePlayLevel;
  }
  return { ...rest, version } as ApogeePlayLevel | AssemblagePlayLevel;
}

export function solutionWords(level: PuzzleAuthoringLevel): string[] {
  return level.puzzle.solutions.map((s) => s.word);
}

/** The planets of a level: an Apogee level's; the other modes have none. */
export function planetsOf(level: PlayLevel | AuthoringLevel): readonly Planet[] {
  return level.mode === 'apogee' ? level.planets : [];
}
